import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle, ExternalLink, ImagePlus, RefreshCw, RotateCcw, RotateCw, ShieldAlert, Trash2, XCircle } from 'lucide-react';
import { Dialog } from '@components/Dialog';
import { Button } from '@components/Button';
import { usePermissions } from '@/hooks/usePermissions';
import { salesOrderService } from '../../services/salesOrderService';
import { formatDateTimeBR } from '../../utils/dateBrasilia';
import {
  ADDRESS_REVIEW_MAX_PHOTO_BYTES,
  ADDRESS_REVIEW_MIN_NOTE,
  AddressReviewStatus,
  OrderAddressReviewView,
  addressReviewNoteRequired,
  addressReviewStatusLabel,
  firstImageFile,
  lookupStatusLabel,
} from '../../types/addressReview';

interface AddressReviewModalProps {
  isOpen: boolean;
  orderId: number;
  orderLabel?: string | number;
  onClose: () => void;
  /** Chamado sempre que a análise muda (busca ou decisão), para o detalhe atualizar a trava. */
  onChanged: (view: OrderAddressReviewView) => void;
}

const HEADINGS = [0, 90, 180, 270];

/** Reduz o print/foto antes de enviar (mesmo pacote das fotos de avaria e de entrega). */
const compressPhoto = async (file: File): Promise<File> => {
  try {
    const imageCompression = (await import('browser-image-compression')).default;
    return await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1600, useWebWorker: true, fileType: 'image/jpeg' });
  } catch {
    return file;
  }
};

/**
 * Análise de fraude — endereço. Mostra a fachada do endereço de entrega no Google Street View
 * (buscada em tempo real pelo ERPApi, nunca armazenada) e registra a aprovação/reprovação, que
 * vai para o histórico do pedido. Em condomínio fechado (sem Street View), aceita uma foto/print
 * da portaria ou fachada, colado (Ctrl+V), arrastado ou escolhido, que basta para aprovar.
 * Regras: ERPApi/docs/funcionalidades/analise-de-endereco.md.
 */
export const AddressReviewModal: React.FC<AddressReviewModalProps> = ({ isOpen, orderId, orderLabel, onClose, onChanged }) => {
  const { checkPermission } = usePermissions();
  const canReview = checkPermission('orders.address_review').hasPermission || checkPermission('*').hasPermission;

  const [view, setView] = useState<OrderAddressReviewView | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');

  // heading undefined = câmera apontada para o endereço (enquadramento da fachada).
  const [heading, setHeading] = useState<number | undefined>(undefined);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const imageUrlRef = useRef<string | null>(null);

  // Foto anexada (portaria/fachada): object URL do proxy da API.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const photoUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const review = view?.review ?? null;
  const hasImagery = review?.lookup_status === 'OK' && !!review?.pano_id;
  const hasPhoto = !!review?.has_photo;

  const releaseImage = () => {
    if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
    imageUrlRef.current = null;
    setImageUrl(null);
  };

  const releasePhoto = () => {
    if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    photoUrlRef.current = null;
    setPhotoUrl(null);
  };

  const loadImage = useCallback(async (h: number | undefined) => {
    setImageLoading(true);
    setImageError(null);
    try {
      const url = await salesOrderService.getAddressReviewImageUrl(orderId, h);
      if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = url;
      setImageUrl(url);
    } catch (e) {
      releaseImage();
      setImageError(e instanceof Error ? e.message : 'Falha ao carregar a imagem');
    } finally {
      setImageLoading(false);
    }
  }, [orderId]);

  const applyView = (v: OrderAddressReviewView) => {
    setView(v);
    onChanged(v);
  };

  useEffect(() => {
    if (!isOpen) {
      releaseImage();
      releasePhoto();
      return;
    }
    setError(null);
    setPhotoError(null);
    setNote('');
    setHeading(undefined);
    setLoading(true);
    salesOrderService.getAddressReview(orderId)
      .then(v => setView(v))
      .catch(e => setError(e instanceof Error ? e.message : 'Falha ao carregar a análise'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId]);

  // A imagem é cobrada pelo Google por exibição: só busca com o modal aberto, quando há
  // panorama e o usuário pode analisar.
  useEffect(() => {
    if (isOpen && canReview && hasImagery) {
      void loadImage(heading);
    } else {
      releaseImage();
    }
  }, [isOpen, canReview, hasImagery, heading, review?.pano_id, review?.address_text, loadImage]);

  // A foto anexada é nossa (blob privado): carrega sempre que a análise diz que há uma.
  const photoStamp = review?.has_photo ? `${review.photo_uploaded_at ?? ''}|${review.photo_uploaded_by ?? ''}` : '';
  useEffect(() => {
    if (!isOpen || !canReview || !photoStamp) {
      releasePhoto();
      return;
    }
    let cancelled = false;
    salesOrderService.getAddressReviewPhotoUrl(orderId)
      .then(url => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = url;
        setPhotoUrl(url);
      })
      .catch(e => !cancelled && setPhotoError(e instanceof Error ? e.message : 'Falha ao carregar a foto anexada'));
    return () => { cancelled = true; };
  }, [isOpen, canReview, orderId, photoStamp]);

  useEffect(() => () => { releaseImage(); releasePhoto(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      applyView(await salesOrderService.refreshAddressReview(orderId));
      setHeading(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao buscar a imagem');
    } finally {
      setRefreshing(false);
    }
  };

  const noteTooShort = note.trim().length < ADDRESS_REVIEW_MIN_NOTE;

  const handleDecide = async (approve: boolean) => {
    setError(null);
    if (addressReviewNoteRequired(approve, hasImagery, hasPhoto) && noteTooShort) {
      setError(approve
        ? `Sem imagem do Street View: anexe uma foto da portaria/fachada ou explique na observação como o endereço foi validado (mínimo ${ADDRESS_REVIEW_MIN_NOTE} caracteres).`
        : `Informe o motivo da reprovação (mínimo ${ADDRESS_REVIEW_MIN_NOTE} caracteres).`);
      return;
    }
    setSaving(true);
    try {
      applyView(await salesOrderService.decideAddressReview(orderId, approve, note.trim()));
      setNote('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao registrar a análise');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = () => {
    if (!review) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Sem análise</span>;
    const cls = review.status === AddressReviewStatus.Approved
      ? 'bg-emerald-100 text-emerald-700'
      : review.status === AddressReviewStatus.Rejected
        ? 'bg-red-100 text-red-700'
        : 'bg-amber-100 text-amber-700';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{addressReviewStatusLabel(review.status)}</span>;
  };

  const busy = saving || refreshing || photoBusy;
  const addressChanged = !!view?.address_changed;
  const canDecide = canReview && !!review && !addressChanged && !view?.inherited_from_order_id;
  // Aprovada, a foto é a prova da aprovação e não muda (o backend também recusa).
  const canEditPhoto = canDecide && review?.status !== AddressReviewStatus.Approved;

  const uploadPhoto = async (file: File | null) => {
    setPhotoError(null);
    if (!file) {
      setPhotoError('Cole ou escolha uma imagem (JPEG, PNG ou WebP).');
      return;
    }
    setPhotoBusy(true);
    try {
      const compressed = await compressPhoto(file);
      if (compressed.size > ADDRESS_REVIEW_MAX_PHOTO_BYTES) {
        setPhotoError('A imagem passa de 10 MB.');
        return;
      }
      applyView(await salesOrderService.uploadAddressReviewPhoto(orderId, compressed, compressed.name || 'portaria.jpg'));
    } catch (e) {
      setPhotoError(e instanceof Error ? e.message : 'Falha ao anexar a foto');
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async () => {
    setPhotoError(null);
    setPhotoBusy(true);
    try {
      applyView(await salesOrderService.deleteAddressReviewPhoto(orderId));
    } catch (e) {
      setPhotoError(e instanceof Error ? e.message : 'Falha ao remover a foto');
    } finally {
      setPhotoBusy(false);
    }
  };

  // Ctrl+V em qualquer ponto do modal: um print da portaria vira a foto anexada. Texto colado
  // (ex.: na observação) segue normal, porque só intercepta quando há imagem na área de transferência.
  const uploadPhotoRef = useRef(uploadPhoto);
  uploadPhotoRef.current = uploadPhoto;
  useEffect(() => {
    if (!isOpen || !canEditPhoto) return;
    const onPaste = (e: ClipboardEvent) => {
      const file = firstImageFile(e.clipboardData?.files);
      if (!file) return;
      e.preventDefault();
      void uploadPhotoRef.current(file);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [isOpen, canEditPhoto]);

  const footer = (
    <div className="flex flex-wrap justify-between gap-3">
      <Button variant="outline" onClick={onClose} disabled={busy}>Fechar</Button>
      {canDecide && (
        <div className="flex gap-3">
          <Button variant="danger" icon={<XCircle size={18} />} onClick={() => handleDecide(false)} loading={saving} disabled={refreshing || photoBusy}>
            Reprovar
          </Button>
          <Button variant="success" icon={<CheckCircle size={18} />} onClick={() => handleDecide(true)} loading={saving} disabled={refreshing || photoBusy}>
            Aprovar endereço
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Análise de fraude — endereço${orderLabel ? ` · Pedido ${orderLabel}` : ''}`}
      footer={footer}
      size="lg"
      closeOnBackdrop={!busy}
      showCloseButton={!busy}
    >
      {loading ? (
        <p className="text-sm text-slate-500">Carregando…</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-slate-700">Status:</span>
            {statusBadge()}
            {view?.is_first_purchase && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">1ª compra</span>
            )}
            {review && !review.is_mandatory && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Opcional (pedido anterior à análise obrigatória)</span>
            )}
          </div>

          {view?.inherited_from_order_id ? (
            <div className="flex gap-2 p-3 rounded-lg bg-slate-50 text-sm text-slate-700">
              <ShieldAlert size={18} className="shrink-0" />
              <span>Este pedido saiu do desmembramento do pedido {view.inherited_from_order_id}; vale a análise dele. Abra o pedido de origem para analisar.</span>
            </div>
          ) : null}

          {view?.blocking && view.block_reason && (
            <div className="flex gap-2 p-3 rounded-lg bg-amber-50 text-sm text-amber-800">
              <AlertTriangle size={18} className="shrink-0" />
              <span>{view.block_reason}</span>
            </div>
          )}

          <div className="text-sm">
            <p className="font-medium text-slate-700">Endereço de entrega</p>
            <p className="text-slate-900">{view?.current_address || 'Pedido sem endereço de entrega'}</p>
            {addressChanged && review && (
              <p className="mt-1 text-amber-700">
                O endereço mudou depois da última busca (consultado: {review.address_text || '—'}). Busque a imagem novamente.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="relative bg-slate-100 aspect-[16/10] flex items-center justify-center">
              {!canReview ? (
                <p className="text-sm text-slate-500 px-4 text-center">Você não tem permissão para ver a imagem da fachada (orders.address_review).</p>
              ) : !review ? (
                <p className="text-sm text-slate-500 px-4 text-center">Este pedido ainda não tem análise. Clique em “Buscar imagem” para consultar o Street View.</p>
              ) : !hasImagery ? (
                <div className="text-sm text-slate-600 px-4 text-center space-y-1">
                  <p className="font-medium">{lookupStatusLabel(review.lookup_status)}</p>
                  {review.lookup_error && <p className="text-slate-500">{review.lookup_error}</p>}
                </div>
              ) : imageLoading ? (
                <p className="text-sm text-slate-500">Carregando imagem…</p>
              ) : imageError ? (
                <p className="text-sm text-red-600 px-4 text-center">{imageError}</p>
              ) : imageUrl ? (
                <img src={imageUrl} alt="Fachada do endereço no Google Street View" className="w-full h-full object-cover" />
              ) : null}
            </div>
            {canReview && hasImagery && (
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-white text-xs text-slate-600">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className={`px-2 py-1 rounded border ${heading === undefined ? 'border-slate-700 text-slate-900' : 'border-slate-200'}`}
                    onClick={() => setHeading(undefined)}
                    disabled={imageLoading}
                    title="Câmera apontada para o endereço"
                  >
                    Fachada
                  </button>
                  {HEADINGS.map(h => (
                    <button
                      key={h}
                      type="button"
                      className={`px-2 py-1 rounded border ${heading === h ? 'border-slate-700 text-slate-900' : 'border-slate-200'}`}
                      onClick={() => setHeading(h)}
                      disabled={imageLoading}
                    >
                      {h}°
                    </button>
                  ))}
                  <button type="button" className="p-1" title="Girar 45° à esquerda" onClick={() => setHeading(((heading ?? 0) + 315) % 360)} disabled={imageLoading}>
                    <RotateCcw size={14} />
                  </button>
                  <button type="button" className="p-1" title="Girar 45° à direita" onClick={() => setHeading(((heading ?? 0) + 45) % 360)} disabled={imageLoading}>
                    <RotateCw size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  {review?.pano_date && <span>Foto de {review.pano_date.split('-').reverse().join('/')}</span>}
                  <span className="font-medium">Imagens © Google</span>
                  {view?.maps_url && (
                    <a href={view.maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                      Abrir no Google Maps <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {canReview && (
            <div className="flex justify-end">
              <Button variant="outline" icon={<RefreshCw size={16} />} onClick={handleRefresh} loading={refreshing} disabled={saving || photoBusy}>
                {review ? 'Buscar imagem novamente' : 'Buscar imagem'}
              </Button>
            </div>
          )}

          {canReview && review && (hasPhoto || canEditPhoto) && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                Foto da portaria / fachada <span className="font-normal text-slate-500">(opcional)</span>
              </p>
              {!hasImagery && canEditPhoto && !hasPhoto && (
                <p className="text-xs text-slate-600">
                  Sem imagem do Street View (comum em condomínio fechado). Anexe um print ou foto da portaria/fachada para aprovar,
                  ou explique na observação como o endereço foi validado.
                </p>
              )}
              {hasPhoto ? (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-slate-100 flex items-center justify-center max-h-80">
                    {photoUrl ? (
                      <img src={photoUrl} alt="Foto anexada da portaria/fachada" className="max-h-80 w-full object-contain" />
                    ) : (
                      <p className="text-sm text-slate-500 py-10">Carregando foto…</p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-white text-xs text-slate-600">
                    <span>
                      Anexada{review.photo_uploaded_by ? ` por ${review.photo_uploaded_by}` : ''}
                      {review.photo_uploaded_at ? ` em ${formatDateTimeBR(review.photo_uploaded_at)}` : ''}
                    </span>
                    {canEditPhoto && (
                      <div className="flex items-center gap-3">
                        <button type="button" className="inline-flex items-center gap-1 text-blue-600 hover:underline" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                          <ImagePlus size={14} /> Trocar
                        </button>
                        <button type="button" className="inline-flex items-center gap-1 text-red-600 hover:underline" onClick={removePhoto} disabled={busy}>
                          <Trash2 size={14} /> Remover
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  className={`rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm cursor-pointer ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 text-slate-600'}`}
                  onClick={() => !busy && fileInputRef.current?.click()}
                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !busy) fileInputRef.current?.click(); }}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); if (!busy) void uploadPhoto(firstImageFile(e.dataTransfer.files)); }}
                >
                  {photoBusy ? 'Enviando foto…' : (
                    <span className="inline-flex flex-col items-center gap-1">
                      <ImagePlus size={20} />
                      <span>Cole aqui um print (Ctrl+V), arraste a imagem ou clique para escolher o arquivo</span>
                    </span>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => { void uploadPhoto(firstImageFile(e.target.files)); e.target.value = ''; }}
              />
              {photoError && <p className="text-sm text-red-600">{photoError}</p>}
            </div>
          )}

          {review?.reviewed_by && (
            <div className="text-sm text-slate-700 p-3 rounded-lg bg-slate-50">
              <p>
                <span className="font-medium">{addressReviewStatusLabel(review.status)}</span> por {review.reviewed_by}
                {review.reviewed_at ? ` em ${formatDateTimeBR(review.reviewed_at)}` : ''}
              </p>
              {review.review_note && <p className="mt-1 whitespace-pre-wrap text-slate-600">{review.review_note}</p>}
            </div>
          )}

          {canDecide && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Observação {hasImagery || hasPhoto ? '(obrigatória para reprovar)' : '(obrigatória sem imagem do Street View ou foto anexada)'}
              </label>
              <textarea
                className="w-full input-field px-2 py-1.5 text-sm"
                rows={3}
                maxLength={1000}
                placeholder="Ex.: fachada compatível com o perfil; ou motivo da reprovação"
                value={note}
                onChange={e => setNote(e.target.value)}
                disabled={busy}
              />
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </Dialog>
  );
};

export default AddressReviewModal;
